import * as Sentry from '@sentry/react'
import { useState, useCallback, useRef } from 'react'
import { useTasting } from './hooks/useTasting'
import { Header } from './components/Header'
import { SetupDialog } from './components/SetupDialog'
import { TabBar, type Tab } from './components/TabBar'
import { ScoreGrid } from './components/InputTab'
import { ResultsTab } from './components/ResultsTab'
import { ChartsTab } from './components/ChartsTab'
import { DevModeDialog } from './components/DevModeDialog'
import { PasswordDialog } from './components/PasswordDialog'
import { getSanityToken, saveTastingToSanity } from './utils/sanity'

function App() {
  const {
    tasting,
    createTasting,
    updateTasting,
    resetTasting,
    addTaster,
    updateTaster,
    removeTaster,
    setTasters,
    assignWine,
    markSaved,
  } = useTasting()
  const [isSetupOpen, setIsSetupOpen] = useState(() => !tasting)
  const [activeTab, setActiveTab] = useState<Tab>('input')
  const [isDevModeOpen, setIsDevModeOpen] = useState(false)
  const [isPasswordOpen, setIsPasswordOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [showSavedToast, setShowSavedToast] = useState(false)
  const resetAfterSave = useRef(false)

  const handleSetupSave = (theme: string, wines: { name: string; price?: number }[], host?: string, date?: string) => {
    if (tasting) {
      updateTasting(theme, wines, host, date)
    } else {
      createTasting(theme, wines, host, date)
    }
  }

  const handleReset = () => {
    resetTasting()
    setIsSetupOpen(true)
  }

  const doSave = useCallback(
    async (token: string) => {
      if (!tasting) return
      setIsSaving(true)
      try {
        await saveTastingToSanity(tasting, token)
        markSaved()
        if (resetAfterSave.current) {
          resetAfterSave.current = false
          resetTasting()
          setIsSetupOpen(true)
        } else {
          setShowSavedToast(true)
          setTimeout(() => setShowSavedToast(false), 2000)
        }
      } catch (err) {
        Sentry.captureException(err)
        console.error('Failed to save to Sanity:', err)
        alert('Failed to save. Please try again.')
      } finally {
        setIsSaving(false)
      }
    },
    [tasting, markSaved, resetTasting],
  )

  const handleSaveToSanity = useCallback(() => {
    const token = getSanityToken()
    if (token) {
      doSave(token)
    } else {
      setIsPasswordOpen(true)
    }
  }, [doSave])

  const handleNewTasting = useCallback(() => {
    if (tasting?._type === 'tasting' && tasting.hasUnsavedChanges) {
      if (window.confirm('You have unsaved changes. Save before starting a new tasting?')) {
        resetAfterSave.current = true
        handleSaveToSanity()
        return
      }
    }
    resetTasting()
    setIsSetupOpen(true)
  }, [tasting, resetTasting, handleSaveToSanity])

  const handleAuthenticated = useCallback(
    (token: string) => {
      setIsPasswordOpen(false)
      doSave(token)
    },
    [doSave],
  )

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {showSavedToast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 px-4 py-2 bg-black text-cream text-sm rounded shadow-lg">
          Saved!
        </div>
      )}
      <Header
        tastingTheme={tasting?.theme ?? null}
        onEditClick={() => setIsSetupOpen(true)}
        onNewTasting={handleNewTasting}
        showSave={tasting?._type === 'tasting' && tasting.hasUnsavedChanges}
        onSave={handleSaveToSanity}
        isSaving={isSaving}
      />
      {tasting && (
        <>
          <TabBar
            activeTab={activeTab}
            onTabChange={setActiveTab}
            onDevMode={() => setIsDevModeOpen(true)}
          />
          <main className="flex-1 overflow-auto">
            {activeTab === 'input' && (
              <ScoreGrid
                tasting={tasting}
                onAddTaster={addTaster}
                onUpdateTaster={updateTaster}
                onRemoveTaster={removeTaster}
              />
            )}
            {activeTab === 'results' && (
              <ResultsTab
                tasting={tasting}
                onAssignWine={assignWine}
                onSaveToSanity={handleSaveToSanity}
                isSaving={isSaving}
                hasUnsavedChanges={tasting.hasUnsavedChanges}
              />
            )}
            {activeTab === 'charts' && <ChartsTab tasting={tasting} />}
          </main>
        </>
      )}
      <SetupDialog
        isOpen={isSetupOpen}
        initialTheme={tasting?.theme}
        initialHost={tasting?.host}
        initialDate={tasting?.date}
        initialWines={tasting?.wines}
        onSave={handleSetupSave}
        onReset={handleReset}
        onClose={() => setIsSetupOpen(false)}
        isEditing={!!tasting}
      />
      {tasting && (
        <DevModeDialog
          isOpen={isDevModeOpen}
          onClose={() => setIsDevModeOpen(false)}
          wineCount={tasting.wines.length}
          existingTasters={tasting.tasters}
          onSetTasters={setTasters}
        />
      )}
      <PasswordDialog
        isOpen={isPasswordOpen}
        onClose={() => setIsPasswordOpen(false)}
        onAuthenticated={handleAuthenticated}
      />
    </div>
  )
}

export default App
